import React, { useCallback, useState } from 'react';
import { CircularProgress, FormGroup, FormLabel } from '@mui/material';
import { useFormContext, Controller, Control } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { AutocompleteProps } from '@mui/material';
import { useDebounce } from '@hooks/useDebounce';
import { Autocomplete } from '@components/AutoComplete';
import { TextField } from '@components/TextField';
import { IOption } from '@components/Filter/filter.utils';
import Colors from '@constants/Colors';

const fetchCitiesAndPostal = (query: string, callback: (data: City[]) => void) => {
    try {
        const isPostal = /^\d+$/.test(query);
        fetch(
            `${process.env.REACT_APP_PUBLIC_API_URI}/here-map/${isPostal ? 'postals' : 'cities'}/${isEmpty(query) ? 'Paris' : query
            }`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        ).then(async response => {
            if (response.ok) {
                const resData = await response.json();

                let suggestions = resData.items?.filter(item => {
                    return !!item.address.city && !!item.address.postalCode;
                });

                //  if query is postal code sort by postal code
                if (isPostal) {
                    suggestions =
                        suggestions.sort((a, b) => {
                            return a.address.postalCode - b.address.postalCode;
                        }) ?? [];
                }

                callback(
                    suggestions?.map((item: any) => {
                        return {
                            ...item,
                            label: `${item.address.city}  ${isPostal ? `(${item.address.postalCode})` : ''}`,
                            value: item.address.city,
                        };
                    }),
                );
            }
        });
    } catch (error) {
        console.log('search-err', error);
    }
};

interface City {
    label: string;
    value: string;
    address?: {
        postalCode?: string | number;
    };
}
interface InputAutocompleteCityProps extends Partial<AutocompleteProps<any, any, any, any>> {
    name: string;
    control?: Control<any>;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    onSelectProp?: (city: string, postalCode: string) => void;
}
const InputAutocompleteCity: React.FC<InputAutocompleteCityProps> = ({
    name,
    control,
    label,
    required,
    onSelectProp,
    ...autocompleteProps
}) => {
    const formContext = useFormContext();
    const resolvedControl = control ?? formContext.control;
    const [options, setOptions] = useState<City[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState<string>('');
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(false); // State to manage dropdown visibility
    const debouncedQuery = useDebounce(query, 500);

    const handleFetchCities = (query: string) => {
        setQuery(query);
    };

    const handleOpen = () => {
        setIsOptionsOpen(true);
    };

    const handleClose = () => {
        setIsOptionsOpen(false);
    };

    const handleEndAdornmentClick = useCallback(() => {
        setIsOptionsOpen(prev => !prev);
    }, []);

    React.useEffect(() => {
        const trimmedQuery = debouncedQuery?.trim();

        // Avoid searching on first mount when the field is pre-filled.
        if (!hasUserTyped || !trimmedQuery) {
            setLoading(false);
            if (!trimmedQuery) {
                setOptions([]);
            }
            return;
        }

        setLoading(true);
        fetchCitiesAndPostal(trimmedQuery, data => {
            setOptions(data);
            setLoading(false);
        });
    }, [debouncedQuery, hasUserTyped]);

    return (
        <Controller
            name={name}
            control={resolvedControl}
            render={({ field, fieldState }) => (
                <Autocomplete
                    {...field}
                    {...autocompleteProps}
                    options={
                        field.value
                            ? [
                                typeof field.value === 'string'
                                    ? { label: field.value, value: field.value }
                                    : field.value,
                                ...options,
                            ]
                            : options
                    } // Ensure current value is in options
                    getOptionLabel={(option: IOption | string) => {
                        if (typeof option === 'string') return option;
                        return option?.label || '';
                    }}
                    isOptionEqualToValue={(option: IOption | string, value: IOption | string) => {
                        const optionValue = typeof option === 'string' ? option : option?.value;
                        const selectedValue = typeof value === 'string' ? value : value?.value;

                        return optionValue === selectedValue;
                    }}
                    onInputChange={(_, value, reason) => {
                        if (reason !== 'input') return;
                        setHasUserTyped(true);
                        handleFetchCities(value);
                    }}
                    onChange={(_, value, reason) => {
                        field.onChange(value);

                        if (reason === 'selectOption' && !Array.isArray(value) && value) {
                            const selectedCity = value as City;
                            const city = selectedCity.value || selectedCity.label || '';
                            const postalCode = selectedCity.address?.postalCode?.toString() || '';

                            onSelectProp?.(city, postalCode);
                            // Keep backward compatibility for callers still using onSelect as a custom callback.
                            const legacyOnSelect = (autocompleteProps as any).onSelect;
                            if (typeof legacyOnSelect === 'function') {
                                legacyOnSelect(city, postalCode);
                            }

                            handleClose();
                        }

                        if (reason === 'clear') {
                            setQuery('');
                            setOptions([]);
                            handleClose();
                        }
                    }}
                    filterSelectedOptions
                    loading={loading}
                    loadingText="Chargement..."
                    noOptionsText={query ? 'Aucun résultat' : 'Tapez pour rechercher...'}
                    open={(options.length > 0 && isOptionsOpen) || loading} // Keep options open
                    onOpen={handleOpen} // Handle dropdown open
                    onClose={(_, reason) => {
                        if (reason === 'selectOption') return;
                        handleClose();
                    }}
                    popupIcon={
                        isOptionsOpen ? (
                            <i
                                className="fi fi-br-check"
                                onClick={handleEndAdornmentClick}
                                style={{ color: Colors.PRIMARY, fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                            />
                        ) : undefined
                    }
                    slotProps={{
                        popupIndicator: {
                            style: {
                                transform: 'none', // Prevent rotation
                            },
                        },
                    }}
                    ChipProps={{
                        deleteIcon: <i className="fi fi-br-cross-small" style={{ fontSize: 12 }} />,
                    }}
                    renderInput={params => (
                        <FormGroup sx={{ width: '100%' }}>
                            <FormLabel error={!!fieldState.error} sx={{ mb: 0.5 }}>
                                {label}
                            </FormLabel>
                            <TextField
                                {...params}
                                size="small"
                                required={required}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                placeholder={autocompleteProps.placeholder || 'Tapez pour rechercher...'}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        </FormGroup>
                    )}
                    sx={{
                        background: theme => theme.palette.dashboard.main,
                    }}
                />
            )}
        />
    );
};

export default InputAutocompleteCity;
